/**
 * AllergyChip — Plan 04-03 / UI-SPEC §"<AllergyChip />".
 *
 * D-06 contract: a quiet ochre Pill that surfaces the household's
 * aggregate allergy + dietary state. Composes a Bokmål list using the
 * existing onboarding allergy chip labels. Hidden when the household has
 * no allergy/dietary entries (no "Husk:" with empty body).
 *
 * NB: D-06 example expectation: nuts + egg + dairy → "Husk: ingen nøtter,
 * ingen egg, laktosefritt". The `dairy` allergy chip in onboarding maps to
 * "laktosefritt" in shopping context (per D-06 example) — we hand-roll a
 * shopping-aware mapping here rather than reusing the generic
 * `allergy.chip.*` labels (which are noun forms: "Nøtter", "Egg",
 * "Melk/laktose"). Same for dietary chips ("low_fodmap" → "lav-FODMAP",
 * "vegetarian" → "vegetar", etc.).
 *
 * Tap target ≥44×32px. Tap opens <AllergyDetailSheet>. Read-only — the
 * chip never validates writes nor blocks input.
 *
 * #157 (redesign steg 7d, D4): brikken er nå skissens amber banner i full
 * bredde — skjoldikon, «Husk: …» og «Detaljer». Hele banneret er én knapp
 * som åpner arket, så målet er stort og testid-en står der den sto. Teksten
 * er fortsatt bare husstandens samlede liste; navnene står i arket (D4,
 * T-04-PII), ikke i banneret slik skissen viser.
 *
 * #148: data comes from useHouseholdDietary — the same rows as the sheet,
 * so the two can't disagree; the household sets are built here.
 *
 * T-04-PII: the chip itself shows only aggregate text, not per-member
 * data. Per-member data lives behind one tap (AllergyDetailSheet).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/primitives/Icon';
import { chipLabels, useHouseholdDietary } from './useHouseholdDietary';
import { AllergyDetailSheet } from './AllergyDetailSheet';

// D-06: shopping-context Bokmål forms for the 8 allergy chips.
// "ingen X" preserves the household-context phrasing.
const ALLERGY_LABELS_SHOPPING_NB: Record<string, string> = {
  nuts: 'ingen nøtter',
  egg: 'ingen egg',
  dairy: 'laktosefritt',
  gluten: 'glutenfritt',
  soy: 'ingen soya',
  shellfish: 'ingen skjell',
  fish: 'ingen fisk',
  celery: 'ingen selleri',
};

const DIETARY_LABELS_SHOPPING_NB: Record<string, string> = {
  low_fodmap: 'lav-FODMAP',
  vegetarian: 'vegetar',
  vegan: 'vegansk',
  halal: 'halal',
  kosher: 'kosher',
};

export function AllergyChip() {
  const { t } = useTranslation();
  const { data: members = [], isError, isPending } = useHouseholdDietary();
  const [sheetOpen, setSheetOpen] = useState(false);

  // Husstandens samlede brikker, i leksikonrekkefølge og uten duplikater.
  // Ukjente id-er tas med som de er (#148), som i arket.
  const labels = [
    ...new Set([
      ...chipLabels(
        members.flatMap((m) => m.allergies.chips),
        Object.keys(ALLERGY_LABELS_SHOPPING_NB),
        (k) => ALLERGY_LABELS_SHOPPING_NB[k] ?? k,
      ),
      ...chipLabels(
        members.flatMap((m) => m.dietary.chips),
        Object.keys(DIETARY_LABELS_SHOPPING_NB),
        (k) => DIETARY_LABELS_SHOPPING_NB[k] ?? k,
      ),
      ...members.flatMap((m) => m.allergies.custom),
      ...members.flatMap((m) => m.dietary.custom),
    ]),
  ];

  const list = labels.join(', ');

  // #148: en feil skjules ikke — en manglende brikke ser ut som «ingen
  // allergier». Trykk åpner arket, som har «Prøv igjen».
  // #157: før første svar (også når spørringen står på pause uten nett)
  // vises banneret med «henter …», så et tomt felt aldri ser ut som «ingen
  // allergier». Arket viser lasting eller feil bak «Detaljer».
  const text = isError
    ? t('shopping.allergy_chip.error')
    : isPending
      ? t('shopping.allergy_chip.loading')
      : list
        ? t('shopping.allergy_chip.composed', { list })
        : null;

  return (
    <>
      {text ? (
        <button
          type="button"
          data-testid={
            isError
              ? 'shopping-allergy-chip-error'
              : isPending
                ? 'shopping-allergy-chip-loading'
                : 'shopping-allergy-chip'
          }
          // «Detaljer» er synlig tekst og skal være med i navnet (WCAG 2.5.3).
          aria-label={t('shopping.allergy_chip.aria', { text })}
          onClick={() => setSheetOpen(true)}
          className="flex min-h-11 w-full items-center gap-2.5 rounded-row bg-warn-bg py-1 pr-1 pl-3.5 text-left text-ink"
        >
          <Icon.AlertShield size={20} className="shrink-0 text-warn" />
          <span
            data-testid="shopping-allergy-chip-text"
            className="min-w-0 flex-1 py-1.5 text-control leading-snug font-semibold"
          >
            {text}
          </span>
          <span className="flex h-11 shrink-0 items-center px-2.5 text-control font-extrabold underline">
            {t('shopping.allergy_chip.details')}
          </span>
        </button>
      ) : null}
      {/* Alltid montert: arket deler spørringen, og en observatør som monteres
          og avmonteres i takt med feiltilstanden henter på nytt i en løkke. */}
      <AllergyDetailSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </>
  );
}
